import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Brain, Zap, TrendingUp, Users, Activity, BarChart3, Clock } from "lucide-react";
import { trpc } from "@/lib/trpc";

export default function AiUsage() {
  const overview = trpc.admin.aiUsage.getOverview.useQuery();
  const byFeature = trpc.admin.aiUsage.getByFeature.useQuery();
  const byTenant = trpc.admin.aiUsage.getByTenant.useQuery({ limit: 20, sortBy: "matches" });

  // CSS bar chart helper — renders a simple inline bar chart
  const maxHistoryValue =
    Math.max(...(byFeature.data?.history.map((h) => h.tenderMatching + h.categorySuggestion) ?? [1]), 1);

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">AI Usage</h1>
          <p className="text-muted-foreground">
            Supervision de l'utilisation des fonctionnalités IA de la plateforme
          </p>
        </div>

        {/* KPI Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Appels IA (30j)</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {overview.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {(overview.data?.totalAiCallsLast30d ?? 0).toLocaleString("fr-FR")}
                  </div>
                  {overview.data?.growthPct !== null && overview.data?.growthPct !== undefined && (
                    <p className={`text-xs mt-1 ${overview.data.growthPct >= 0 ? "text-success" : "text-destructive"}`}>
                      {overview.data.growthPct >= 0 ? "+" : ""}
                      {overview.data.growthPct}% vs mois précédent
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Taux d'adoption</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {overview.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{overview.data?.adoptionRate ?? 0}%</div>
                  <p className="text-xs text-muted-foreground mt-1">des tenants actifs ce mois</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Matching AO (30j)</CardTitle>
              <Brain className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              {overview.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {(overview.data?.features.tenderMatching.callsLast30d ?? 0).toLocaleString("fr-FR")}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">matches calculés</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Sugg. catégories (30j)</CardTitle>
              <TrendingUp className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              {overview.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {(overview.data?.features.categorySuggestion.callsLast30d ?? 0).toLocaleString("fr-FR")}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">suggestions générées</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Historique mensuel — CSS bar chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Évolution mensuelle — 12 derniers mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            {byFeature.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-primary" />
                    Matching AO
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-success" />
                    Suggestion catégories
                  </span>
                </div>
                <div className="flex items-end gap-1 h-40">
                  {byFeature.data?.history.map((h) => {
                    const totalH = h.tenderMatching + h.categorySuggestion;
                    const matchingPct = maxHistoryValue > 0 ? (h.tenderMatching / maxHistoryValue) * 100 : 0;
                    const suggPct = maxHistoryValue > 0 ? (h.categorySuggestion / maxHistoryValue) * 100 : 0;
                    return (
                      <div key={h.month} className="flex-1 flex flex-col items-center gap-0.5 group">
                        <div
                          className="relative w-full flex flex-col justify-end gap-0.5 transition-opacity"
                          style={{ height: "120px" }}
                          title={`${h.month}: ${totalH} appels`}
                        >
                          <div
                            className="w-full bg-success rounded-t-sm opacity-80 group-hover:opacity-100 transition-opacity"
                            style={{ height: `${suggPct}%` }}
                          />
                          <div
                            className="w-full bg-primary rounded-t-sm opacity-80 group-hover:opacity-100 transition-opacity"
                            style={{ height: `${matchingPct}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                          {h.month}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fonctionnalités IA */}
        <div>
          <h2 className="text-base font-semibold mb-3">Fonctionnalités IA Constrigo</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {byFeature.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <Skeleton className="h-24 w-full" />
                  </CardContent>
                </Card>
              ))
            ) : (
              byFeature.data?.features.map((feature) => (
                <Card key={feature.key}>
                  <CardHeader className="flex flex-row items-start gap-3 pb-2">
                    <div className="mt-0.5">
                      {feature.key === "tender_matching" && <Brain className="h-5 w-5 text-primary" />}
                      {feature.key === "category_suggestion" && <TrendingUp className="h-5 w-5 text-success" />}
                      {feature.key === "ai_assistant" && <Zap className="h-5 w-5 text-warning" />}
                      {feature.key === "report_generation" && <BarChart3 className="h-5 w-5 text-primary" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium">{feature.label}</CardTitle>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${
                            feature.status === "active"
                              ? "border-success/30 text-success bg-success/10"
                              : "border-border text-muted-foreground bg-muted/40"
                          }`}
                        >
                          {feature.status === "active" ? "Actif" : "En attente"}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {feature.status === "active" ? (
                      <div className="grid grid-cols-2 gap-3 mt-1">
                        <div className="p-2 bg-muted rounded text-center">
                          <div className="text-lg font-bold">
                            {feature.totalCalls.toLocaleString("fr-FR")}
                          </div>
                          <div className="text-xs text-muted-foreground">Total appels</div>
                        </div>
                        {"conversionRate" in feature && (
                          <div className="p-2 bg-muted rounded text-center">
                            <div className="text-lg font-bold">{feature.conversionRate}%</div>
                            <div className="text-xs text-muted-foreground">
                              {feature.key === "tender_matching" ? "Taux GO" : "Score ≥ 70%"}
                            </div>
                          </div>
                        )}
                        {"avgScore" in feature && feature.avgScore !== undefined && (
                          <div className="p-2 bg-muted rounded text-center col-span-2">
                            <div className="text-lg font-bold">{feature.avgScore}/100</div>
                            <div className="text-xs text-muted-foreground">Score moyen matching</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground p-2 bg-muted/40 rounded">
                        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                        Instrumentation en attente — données disponibles après déploiement du tracking
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Top tenants */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Top tenants par usage IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            {byTenant.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : byTenant.data && byTenant.data.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs">
                      <th className="text-left pb-2 font-medium">Entreprise</th>
                      <th className="text-right pb-2 font-medium">Matches totaux</th>
                      <th className="text-right pb-2 font-medium">Matches 30j</th>
                      <th className="text-right pb-2 font-medium">Score moyen</th>
                      <th className="text-right pb-2 font-medium">Décisions GO</th>
                      <th className="text-right pb-2 font-medium">Taux adoption</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTenant.data.map((row) => (
                      <tr key={row.companyId} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 font-medium">{row.companyName}</td>
                        <td className="py-2 text-right">{row.totalMatchesAllTime.toLocaleString("fr-FR")}</td>
                        <td className="py-2 text-right">{row.matchesLast30d.toLocaleString("fr-FR")}</td>
                        <td className="py-2 text-right">
                          <span
                            className={`font-medium ${
                              row.avgMatchScore >= 70
                                ? "text-success"
                                : row.avgMatchScore >= 50
                                ? "text-warning"
                                : "text-muted-foreground"
                            }`}
                          >
                            {row.avgMatchScore}
                          </span>
                        </td>
                        <td className="py-2 text-right">{row.goDecisions}</td>
                        <td className="py-2 text-right">
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full border ${
                              row.adoptionScore >= 20
                                ? "border-success/30 text-success bg-success/10"
                                : "border-border text-muted-foreground"
                            }`}
                          >
                            {row.adoptionScore}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                Aucune donnée d'usage disponible
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
